import React, { useCallback, useMemo } from "react"
import { ModalContainerComponent } from "../types"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    inviteAllUsersToWorkspace,
    listMembersForWorkspace,
    selectAllMembers,
    selectInviteModalId,
    updateMemberPermissionsHandler,
} from "features/entity-caching/members/UserManagementSlice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import {
    selectActiveWorkspace,
    selectTagById,
    selectLegacyWorkspaceTree,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { InviteEverywhereModal } from "./InviteEverywhereModal"
import { ID, Permissions } from "@nova/constants"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectAllFires, selectFireById } from "features/entity-caching/fires/selectors"
import { convertDataTypeToInviteDestination } from "./module"
import * as Icons from "@nova/component-icons"
import { findTagAncestors } from "features/data-management/workspace/module"
import { OrganizationExtended, OrganizationPermissions, WorkspaceMember } from "@nova/schemas"
import { retrievePermissionsFromTagTree } from "@nova/util-tag-tree"
import { hideGlobalModal } from "runlevel/actions"

export type PermissionUpdates = Record<
    ID,
    { accessLevel: "workspace" | "subRole"; newPermission: Permissions; entityId?: ID }
>

export const InviteEverywhereModalContainer = () => {
    const dispatch = useAppDispatch()

    const allMembers = useAppSelector(selectAllMembers())
    const inviteModalId = useAppSelector(selectInviteModalId())

    const activeWorkspace = useAppSelector(selectActiveWorkspace())
    const activeWorkspaceId = activeWorkspace?.id
    const sidebarTree = useAppSelector(selectLegacyWorkspaceTree)

    const allProjects = useAppSelector(selectAllFires)
    const activeProjectId = useAppSelector(selectActivatedFireId)
    const activeProject = useAppSelector(selectFireById(inviteModalId || activeProjectId))

    const activeTag = useAppSelector(selectTagById(inviteModalId || ""))

    const filteredTags = findTagAncestors(sidebarTree || [], activeProject?.tagId || activeTag?.id)

    const sendAllInvites = useCallback(
        async (
            workspace: OrganizationExtended,
            users: string[],
            permissions: OrganizationPermissions
        ) => {
            await dispatch(
                inviteAllUsersToWorkspace({
                    workspaceId: workspace.id,
                    emailAddresses: users,
                    permissions,
                })
            ).unwrap()

            //assuming here only one entity is being invited to at a time
            const inviteEntityId = permissions.subRoles?.length
                ? permissions.subRoles[0].id
                : undefined
            const existingEmails = allMembers[workspace.id]
                .filter((member) => {
                    //members with higher level access can't receive more permissions for a sub entity (they already have everything)
                    if (!!member.permissions.workspaceAccess) return true
                    if (!inviteEntityId) return false
                    const project = allProjects[inviteEntityId]
                    const permission = retrievePermissionsFromTagTree(
                        member.permissions,
                        workspace.tagTree,
                        project,
                        inviteEntityId
                    )
                    return !!permission
                })
                .map((member) => ("email" in member ? member.email : member.userId))

            const isDuplicatingEmails = users.some((email) => existingEmails.includes(email))
            dispatch(
                setGlobalNotificationContext({
                    message: !isDuplicatingEmails
                        ? "Invitation sent"
                        : "Invitation sent. Some collaborators already\nhave access and were not notified.",
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 6000,
                })
            )
        },
        [dispatch, allMembers, allProjects]
    )

    const handlePermissionUpdate = useCallback(
        (
            member: WorkspaceMember,
            newPermission: Permissions,
            accessLevel: "workspace" | "subRole",
            entityId?: ID
        ) => {
            if (!activeWorkspaceId) return
            dispatch(
                updateMemberPermissionsHandler({
                    workspaceId: activeWorkspaceId,
                    member,
                    accessLevel,
                    newPermission,
                    entityId,
                })
            )
        },
        [activeWorkspaceId, dispatch]
    )

    const handleAllPermissionsUpdate = useCallback(
        (members: WorkspaceMember[], permissionsUpdates: PermissionUpdates) => {
            members.forEach((member) => {
                const updates = permissionsUpdates[member.userId]
                if (!updates) return
                const { accessLevel, newPermission, entityId } = updates
                handlePermissionUpdate(member, newPermission, accessLevel, entityId)
            })
        },
        [handlePermissionUpdate]
    )

    const getMembers = useCallback(
        async (workspaceId: string) => {
            await dispatch(listMembersForWorkspace({ workspaceId }))
        },
        [dispatch]
    )

    const destinations = []
    const reversedTags = useMemo(() => filteredTags.reverse(), [filteredTags])
    if (activeProject)
        destinations.push({
            ...convertDataTypeToInviteDestination(activeProject),
            icon: Icons.Pen4,
        })
    if (activeTag)
        destinations.push({ ...convertDataTypeToInviteDestination(activeTag), icon: Icons.Folder })
    if (filteredTags)
        reversedTags.forEach((tag) => {
            if (tag.id !== activeTag?.id)
                destinations.push({ ...tag, icon: Icons.Folder, fullName: tag.name })
        })
    if (!activeWorkspace) return null
    destinations.push({
        ...convertDataTypeToInviteDestination(activeWorkspace),
        icon: Icons.Language,
    })
    return (
        <InviteEverywhereModal
            inviteDestinations={destinations}
            closeModal={() => dispatch(hideGlobalModal())}
            sendInvites={sendAllInvites}
            updatePermissions={handleAllPermissionsUpdate}
            allMembers={allMembers}
            getMembers={getMembers}
        />
    )
}

export default InviteEverywhereModalContainer as ModalContainerComponent
